import React, { useMemo, useState } from 'react';
import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { colors } from '../../styles/theme';
import { getCampaignBreakdowns } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { readMetaPlatforms, sortMetaPlacements } from '../metaAnalysis';
import { formatMetaCount, metaPlural } from '../metaFormat';
import { isMetaTodayPeriod } from '../metaPeriod';
import { MetaBarList, MetaBlockState, MetaCard, MetaEmptyLine, MetaFootnote, MetaSoftNotice, MetaTable } from './MetaDataParts';
import { MetaBarsSkeleton, MetaTableSkeleton } from './MetaSkeletons';
import { metaAdsQuietButtonSx, metaViewToggleSx } from './metaAdsUi.styles';
import { metaCostText, metaFractions, metaRowValueText, validMetaScope } from './metaAnalysisUi';

const PLACEMENTS_PAGE = 8;
const COST_ROWS = 5;
const SORTS = [
  { key: 'impressions', label: 'Más vistas' },
  { key: 'contacts', label: 'Más contactos' },
];

function PlatformsCard({ read, state }) {
  const { platforms, metric, currency } = read;
  const fractions = metaFractions(platforms.map((p) => p[metric]));
  return (
    <MetaCard title="Plataformas" caption={metric === 'contacts' ? 'Contactos · Costo por contacto' : 'Impresiones'} testId="meta-platforms-platforms">
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-platforms-platforms-state" skeleton={<MetaBarsSkeleton rows={3} />}>
        {platforms.length === 0 ? (
          <MetaEmptyLine testId="meta-platforms-platforms-empty">Todavía no hay datos de plataformas para este período.</MetaEmptyLine>
        ) : (
          <MetaBarList
            testId="meta-platforms-bars"
            rows={platforms.map((p, i) => ({ key: p.key || p.label, label: p.label, fraction: fractions[i], valueText: metaRowValueText(p, metric, currency) }))}
          />
        )}
      </MetaBlockState>
    </MetaCard>
  );
}

function PlacementsCard({ read, state, sort, onSortChange }) {
  const [shown, setShown] = useState(PLACEMENTS_PAGE);
  const sorted = useMemo(() => sortMetaPlacements(read.placements, sort), [read.placements, sort]);
  const rows = sorted.slice(0, shown).map((p) => ({
    key: p.key,
    cells: { name: p.label, impressions: formatMetaCount(p.impressions), contacts: formatMetaCount(p.contacts || 0) },
  }));
  return (
    <MetaCard
      title="Ubicaciones"
      testId="meta-platforms-placements"
      action={(
        <ToggleButtonGroup exclusive size="small" value={sort} onChange={(_e, next) => { if (next) onSortChange(next); }} aria-label="Ordenar ubicaciones" sx={metaViewToggleSx} data-testid="meta-platforms-sort">
          {SORTS.map((s) => <ToggleButton key={s.key} value={s.key} data-testid={`meta-platforms-sort-${s.key}`}>{s.label}</ToggleButton>)}
        </ToggleButtonGroup>
      )}
    >
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-platforms-placements-state" skeleton={<MetaTableSkeleton rows={5} />}>
        {sorted.length === 0 ? (
          <MetaEmptyLine testId="meta-platforms-placements-empty">Todavía no hay datos de ubicaciones para este período.</MetaEmptyLine>
        ) : (
          <>
            <MetaTable
              testId="meta-platforms-table"
              columns={[{ key: 'name', label: 'Ubicación' }, { key: 'impressions', label: 'Impresiones' }, { key: 'contacts', label: 'Contactos' }]}
              rows={rows}
            />
            {sorted.length > shown ? (
              <Button size="small" onClick={() => setShown((n) => n + PLACEMENTS_PAGE)} sx={{ ...metaAdsQuietButtonSx, color: colors.primaryLight }} data-testid="meta-platforms-more">
                {`Mostrar ${metaPlural(Math.min(PLACEMENTS_PAGE, sorted.length - shown), 'ubicación más', 'ubicaciones más')}`}
              </Button>
            ) : null}
            <MetaFootnote testId="meta-platforms-placements-note">
              {'Impresiones: veces que se mostró el anuncio.'}
              <br />
              {'Contactos: conversaciones de WhatsApp + formularios.'}
            </MetaFootnote>
          </>
        )}
      </MetaBlockState>
    </MetaCard>
  );
}

/** Costo por contacto de las ubicaciones con más contactos (solo las que tienen costo: una sola moneda). */
function CostCard({ read, state }) {
  const rows = useMemo(() => sortMetaPlacements(read.placements, 'contacts')
    .filter((p) => p.costPerContact != null && (p.contacts || 0) > 0).slice(0, COST_ROWS), [read.placements]);
  if (state.status === 'ready' && rows.length === 0) return null;
  const fractions = metaFractions(rows.map((r) => r.costPerContact));
  return (
    <MetaCard title="Costo por contacto por ubicación" testId="meta-platforms-cost">
      <MetaBlockState status={state.status} error={state.error} onRetry={state.reload} testId="meta-platforms-cost-state" skeleton={<MetaBarsSkeleton rows={3} />}>
        <MetaBarList
          testId="meta-platforms-cost-bars"
          rows={rows.map((r, i) => ({ key: r.key, label: r.label, fraction: fractions[i], tone: 'light', valueText: metaCostText(r.costPerContact, read.currency) }))}
        />
      </MetaBlockState>
    </MetaCard>
  );
}

/**
 * Análisis · Plataformas: plataformas, ubicaciones y costo por contacto por ubicación. Un solo
 * pedido (`breakdown=platform_position`), solo al abrir la pantalla.
 */
export default function MetaPlatformsView({ campaigns, scopeCampaignId, period, ownerUserId = null }) {
  const campaignId = validMetaScope(campaigns, scopeCampaignId);
  const state = useMetaRead(
    (signal) => getCampaignBreakdowns({ period, campaignId, breakdown: 'platform_position', ownerUserId, signal }),
    { deps: [period?.since, period?.until, campaignId, ownerUserId] },
  );
  const read = useMemo(() => readMetaPlatforms(state.res), [state.res]);
  const [sort, setSort] = useState('contacts');
  if (state.status === 'unavailable') {
    return (
      <PanelEmptyState
        icon={DevicesOutlinedIcon}
        title="Todavía no hay datos de plataformas"
        hint="Todavía no están disponibles en este entorno. Se van a ver acá apenas estén."
        sx={{ minHeight: 240 }}
        testId="meta-analysis-platforms-empty"
      />
    );
  }
  return (
    <>
      {isMetaTodayPeriod(period) ? (
        <MetaSoftNotice testId="meta-platforms-today">Hoy los datos de plataformas pueden estar incompletos; se completan mañana.</MetaSoftNotice>
      ) : null}
      <PlatformsCard read={read} state={state} />
      <PlacementsCard read={read} state={state} sort={sort} onSortChange={setSort} />
      <CostCard read={read} state={state} />
    </>
  );
}
